import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

const BASE = "https://sellavo.com";

export const Route = createFileRoute("/jamfor/$tjanst")({
  loader: async ({ params }) => {
    const { findComparison } = await import("@/lib/jamforelser");
    const item = findComparison(params.tjanst);
    if (!item) throw notFound();
    return { item };
  },
  head: ({ params, loaderData }) => {
    const item = loaderData?.item;
    if (!item) {
      return {
        meta: [
          { title: "Jämförelsen finns inte | Sellavo" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const title = `${item.metaTitle} | Sellavo`;
    const url = `${BASE}/jamfor/${params.tjanst}`;
    return {
      meta: [
        { title },
        { name: "description", content: item.description },
        { property: "og:title", content: title },
        { property: "og:description", content: item.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: item.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: BASE },
              { "@type": "ListItem", position: 2, name: "Jämförelser", item: `${BASE}/jamfor` },
              { "@type": "ListItem", position: 3, name: item.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ComparePage,
  notFoundComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="font-heading text-2xl font-extrabold text-foreground">
        Jämförelsen finns inte
      </h1>
      <Button asChild className="mt-6">
        <Link to="/jamfor">Se alla jämförelser</Link>
      </Button>
    </main>
  ),
});

function ComparePage() {
  const { item } = Route.useLoaderData();

  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <nav aria-label="Brödsmulor" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Start
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/jamfor" className="hover:text-foreground">
          Jämförelser
        </Link>
      </nav>

      <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
        {item.h1}
      </h1>

      <div className="mt-6 rounded-lg border border-primary/25 bg-primary/5 p-5">
        <p className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
          Det korta svaret
        </p>
        <p className="mt-2 text-sm leading-relaxed text-foreground sm:text-base">{item.summary}</p>
      </div>

      <div className="mt-6 space-y-4">
        {item.intro.map((p) => (
          <p key={p} className="text-base leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}
      </div>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground">
          {item.name} och Sellavo sida vid sida
        </h2>
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th scope="col" className="px-4 py-2.5 text-left font-heading font-semibold">
                  &nbsp;
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-heading font-semibold">
                  {item.name}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-heading font-semibold">
                  Sellavo
                </th>
              </tr>
            </thead>
            <tbody>
              {item.rows.map((row) => (
                <tr key={row.label} className="border-t border-border align-top">
                  <th scope="row" className="px-4 py-2.5 text-left font-medium text-foreground">
                    {row.label}
                  </th>
                  <td className="px-4 py-2.5 text-muted-foreground">{row.other}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{row.sellavo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="mt-10 space-y-8">
        {item.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-heading text-xl font-bold text-foreground">{section.heading}</h2>
            {section.body.map((p) => (
              <p
                key={p}
                className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base"
              >
                {p}
              </p>
            ))}
          </section>
        ))}
      </div>

      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground">Vanliga frågor</h2>
        <dl className="mt-4 space-y-4">
          {item.faq.map((f) => (
            <div key={f.q} className="rounded-lg border border-border bg-card p-5">
              <dt className="font-heading text-base font-semibold text-foreground">{f.q}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="mt-12 rounded-lg border border-border bg-card p-6 text-center">
        <p className="font-heading text-lg font-bold text-foreground">Testa själv</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Fota prylen så skriver vi annonsen och föreslår pris. Se vad ett konto kostar på
          prissidan.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to="/skapa">Skapa annons</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/priser">Se priser</Link>
          </Button>
        </div>
      </div>
    </main>
  );
}
